import type { HTMLAttributes, Ref } from 'react';
import React from 'react';
import type { ClassNameProp, EHealthIcon, MaterialIcon } from '..';
import { isEmptyString, mergeRefs, useIsStorybook } from '../../util';
import { useFontBoostSize } from './internal/useFontBoostSize';

/**
 * Type for a valid name of an Icon.
 * This includes EHealthIcons and MaterialIcons.
 */
export type ValidIconName = EHealthIcon | MaterialIcon;

/**
 * Props for an {@link Icon} component.
 */
export interface IconProps
  extends ClassNameProp,
    Pick<HTMLAttributes<HTMLElement>, 'aria-hidden' | 'role' | 'style' | 'id'> {
  /**
   * {@link HTMLAttributes#aria-label}, mandatory attribute for {@link Icon}.
   */
  'aria-label': HTMLAttributes<HTMLElement>['aria-label'];
  /**
   * Name of the icon.
   */
  iconName: ValidIconName;
  /**
   * Multiplier applied to the icon's own font-size when computing its width
   * on mobile app WebViews, to work around Android's font-boost not affecting em-based sizing.
   */
  fontBoostWidthMultiplier?: number;
  /**
   * Multiplier applied to the icon's own font-size when computing its height
   * on mobile app WebViews, to work around Android's font-boost not affecting em-based sizing.
   */
  fontBoostHeightMultiplier?: number;
}

const EHEALTH_ICON_PREFIX = 'ehealthicon-';
const MATERIAL_ICON_PREFIX = 'materialicon-';

/**
 * A simple Icon element.
 *
 * @param properties icon properties the <code>'ehealthicon-'</code> prefix should be omitted from the iconName if the icon is hard-coded
 * @constructor
 * @return {@link React.JSX.Element} created for the icon.
 */
export function Icon({
  className = '',
  iconName,
  style,
  'aria-label': ariaLabel,
  role = isEmptyString(ariaLabel) ? 'presentation' : 'img',
  'aria-hidden': ariaHidden,
  fontBoostWidthMultiplier,
  fontBoostHeightMultiplier,
  ref,
  ...props
}: Readonly<IconProps> & {
  ref?: Ref<SVGSVGElement | HTMLDivElement>;
}): React.JSX.Element {
  const boostRef = useFontBoostSize<SVGSVGElement | HTMLDivElement>(
    fontBoostWidthMultiplier,
    fontBoostHeightMultiplier
  );
  const storyBookContext = useIsStorybook();

  // this is needed for icons returned at runtime from the backend
  if (!iconName.startsWith(MATERIAL_ICON_PREFIX)) {
    // ensures that ehealthicon- prefix is assigned for iconName without materialicon- prefix
    iconName = iconName.replace(
      RegExp('(' + EHEALTH_ICON_PREFIX + ')?'),
      EHEALTH_ICON_PREFIX
    ) as EHealthIcon;
  }
  const iconClassName = ['ehs-icon', className, iconName]
    .filter(Boolean)
    .join(' ');

  if (iconName.startsWith(EHEALTH_ICON_PREFIX)) {
    let iconPath = 'o/WebResourceProviderHook/icons/ehealth-icons.svg';
    if (!storyBookContext) iconPath = '/' + iconPath;
    return (
      <svg
        {...props}
        ref={mergeRefs([boostRef, ref as Ref<SVGSVGElement>])}
        style={style}
        className={iconClassName}
        role={role}
        aria-label={isEmptyString(ariaLabel) ? undefined : ariaLabel}
        aria-hidden={ariaHidden}
      >
        <use
          href={
            iconPath +
            '?version=' +
            window.SenseConstants?.version +
            '#' +
            iconName
          }
        />
      </svg>
    );
  } else {
    return (
      <div
        {...props}
        ref={mergeRefs([boostRef, ref as Ref<HTMLDivElement>])}
        style={style}
        className={iconClassName}
        role={role}
        aria-label={isEmptyString(ariaLabel) ? undefined : ariaLabel}
        aria-hidden={ariaHidden}
      />
    );
  }
}
